import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { AuthProvider } from './contexts/AuthContext';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';

// Eagerly load critical pages
import MyDojoLandingPage from './pages/my-dojo-landing-page';
import LandingPage from './pages/landing-page';
import LoginPage from './pages/login';
import AcademyRegistration from './pages/academy-registration';

// Lazy load dashboard pages for performance
const AttendanceTracking = lazy(() => import('./pages/attendance-tracking'));
const StudentManagement = lazy(() => import('./pages/student-management'));
const StudentPortal = lazy(() => import('./pages/student-portal'));
const StudentProgress = lazy(() => import('./pages/student-portal/progress'));
const StudentSchedule = lazy(() => import('./pages/student-portal/schedule'));
const StudentProfile = lazy(() => import('./pages/student-portal/profile'));
const FinancialDashboard = lazy(() => import('./pages/financial-dashboard'));
const OwnerDashboard = lazy(() => import('./pages/owner-dashboard'));
const CoachDashboard = lazy(() => import('./pages/coach-dashboard'));
const BeltProgression = lazy(() => import('./pages/belt-progression'));
const ScheduleManagement = lazy(() => import('./pages/schedule-management'));
const CommunicationCenter = lazy(() => import('./pages/communication-center'));
const SuperAdminDashboard = lazy(() => import('./pages/super-admin-dashboard'));
const AcademyBillingManagement = lazy(() => import('./pages/academy-billing-management'));
const CompetitionManagement = lazy(() => import('./pages/competition-management'));
const ReceptionCheckInKiosk = lazy(() => import('./pages/reception-check-in-kiosk'));
const AcademyBrandCustomization = lazy(() => import('./pages/academy-brand-customization'));
const StaffPortalDashboard = lazy(() => import('./pages/staff-portal-dashboard'));
const PaymentConfirmationPix = lazy(() => import('./pages/payment-confirmation-pix'));
const PaymentConfirmationBoleto = lazy(() => import('./pages/payment-confirmation-boleto'));
const AcademySetupWizard = lazy(() => import('./pages/academy-setup-wizard'));
const DemoLeadsDashboard = lazy(() => import('./pages/demo-leads-dashboard'));
const HelpCenterManual = lazy(() => import('./pages/help-center-manual'));
const AcademyCreationSuccess = lazy(() => import('./pages/academy-creation-success'));
const StudentAnalyticsDashboard = lazy(() => import('./pages/student-analytics-dashboard'));
const PersonalizedDevelopmentDashboard = lazy(() => import('./pages/personalized-development-dashboard'));
const AITrainingPlanGenerator = lazy(() => import('./pages/ai-training-plan-generator'));
const TechniqueImprovementAnalytics = lazy(() => import('./pages/technique-improvement-analytics'));
const AIPoweredStudentDevelopmentPlanner = lazy(() => import('./pages/ai-powered-student-development-planner'));
const InteractiveUserManualWithAIAssistant = lazy(() => import('./pages/interactive-user-manual-with-ai-assistant'));
const TermsOfService = lazy(() => import('./pages/terms-of-service'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const ExpenseManagement = lazy(() => import('./pages/expense-management'));
const DigitalWaiverSystem = lazy(() => import('./pages/digital-waiver-system'));

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Inner component that uses router hooks
const RoutesContent = () => {
  useGoogleAnalytics();
  
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <RouterRoutes>
          {/* Define your route here */}
          <Route path="/" element={<MyDojoLandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/academy-registration" element={<AcademyRegistration />} />
          <Route path="/attendance-tracking" element={<AttendanceTracking />} />
          <Route path="/student-management" element={<StudentManagement />} />
          <Route path="/student-portal" element={<StudentPortal />} />
          <Route path="/student-portal/progress" element={<StudentProgress />} />
          <Route path="/student-portal/schedule" element={<StudentSchedule />} />
          <Route path="/student-portal/profile" element={<StudentProfile />} />
          <Route path="/staff-portal-dashboard" element={<StaffPortalDashboard />} />
          <Route path="/financial-dashboard" element={<FinancialDashboard />} />
          <Route path="/owner-dashboard" element={<OwnerDashboard />} />
          <Route path="/coach-dashboard" element={<CoachDashboard />} />
          <Route path="/belt-progression" element={<BeltProgression />} />
          <Route path="/schedule-management" element={<ScheduleManagement />} />
          <Route path="/communication-center" element={<CommunicationCenter />} />
          <Route path="/academy-billing-management" element={<AcademyBillingManagement />} />
          <Route path="/academy-brand-customization" element={<AcademyBrandCustomization />} />
          <Route path="/super-admin-dashboard" element={<SuperAdminDashboard />} />
          <Route path="/competition-management" element={<CompetitionManagement />} />
          <Route path="/reception-check-in-kiosk" element={<ReceptionCheckInKiosk />} />
          <Route path="/payment-confirmation-pix" element={<PaymentConfirmationPix />} />
          <Route path="/payment-confirmation-boleto" element={<PaymentConfirmationBoleto />} />
          <Route path="/academy-setup-wizard" element={<AcademySetupWizard />} />
          <Route path="/demo-leads-dashboard" element={<DemoLeadsDashboard />} />
          <Route path="/help-center-manual" element={<HelpCenterManual />} />
          <Route path="/academy-creation-success" element={<AcademyCreationSuccess />} />
          <Route path="/student-analytics-dashboard" element={<StudentAnalyticsDashboard />} />
          <Route path="/ai-training-plan-generator" element={<AITrainingPlanGenerator />} />
          <Route path="/technique-improvement-analytics" element={<TechniqueImprovementAnalytics />} />
          <Route path="/personalized-development-dashboard" element={<PersonalizedDevelopmentDashboard />} />
          <Route path="/ai-powered-student-development-planner" element={<AIPoweredStudentDevelopmentPlanner />} />
          <Route path="/interactive-user-manual-with-ai-assistant" element={<InteractiveUserManualWithAIAssistant />} />
          <Route path="/expense-management" element={<ExpenseManagement />} />
          <Route path="/digital-waiver-system" element={<DigitalWaiverSystem />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </Suspense>
    </ErrorBoundary>
  );
};

const Routes = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RoutesContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default Routes;